import React from "react";
import PolicyTemplate from "./PolicyTemplate";
import siteConfig from "../config/siteConfig";

const brand = siteConfig.brandName;

const privacyPolicy = {
  eyebrow: "Privacy & digital",
  title: "Privacy policy",
  intro: `Your trust matters to ${brand}. This policy explains what personal information we collect, where it comes from, why we use it, when it is disclosed, and the choices available to you.`,
  updated: "October 23, 2026",
  sections: [
    {
      id: "privacy-scope",
      title: "Who we are and scope",
      paragraphs: [
        `${brand} operates this online storefront from ${siteConfig.operatingRegion.country} and is responsible for personal information handled for the purposes described here. This policy applies when you browse, place an order, contact client care, submit a request, or otherwise interact with this storefront.`,
        "A payment provider, carrier, or linked service may separately determine how it uses information for its own purposes. Review that provider’s notice when you leave this storefront or use its service.",
      ],
      email: true,
      emailSuffix: " for privacy questions or requests.",
    },
    {
      id: "privacy-categories",
      title: "Information we collect",
      bullets: [
        "identifiers and contact details, such as name, email, delivery and billing address, and order number;",
        "commercial information, such as products, quantities, options, totals, returns, refunds, and support history;",
        "payment-related information, such as payment status and limited transaction references supplied by the payment provider, but not complete card numbers or security codes;",
        "communications, requests, photographs, reviews, or other material you choose to send; and",
        "technical information, such as IP address, browser, device, approximate location, referring page, timestamps, security events, and interactions recorded by hosting or service infrastructure.",
      ],
    },
    {
      id: "privacy-sources",
      title: "Where information comes from",
      paragraphs: [
        "We receive information directly from you, automatically from your browser or device, and from providers that support payment, fraud prevention, hosting, delivery, customer service, and transaction administration. Cart and wishlist selections are stored in your browser by this storefront unless and until they become part of an order or message.",
      ],
    },
    {
      id: "privacy-use",
      title: "How we use information",
      bullets: [
        "provide the storefront, remember your selections, process payment, fulfill orders, and communicate delivery status;",
        "respond to returns, warranty, accessibility, privacy, safety, and other support requests;",
        "verify transactions, prevent fraud, secure the site, and enforce purchase limits and policies;",
        "maintain tax, accounting, compliance, dispute, and business records;",
        "debug, measure, and improve products, pages, checkout, and customer service; and",
        "send direct marketing only where the required notice and consent have been provided, with a way to opt out.",
      ],
    },
    {
      id: "privacy-bases",
      title: "Legal bases where required",
      paragraphs: [
        "Where a law requires a legal basis, we rely on performance of a contract or steps you request before purchase; compliance with legal obligations; legitimate interests such as security, fraud prevention, service improvement, and legal claims where those interests are not overridden; or consent for an optional use. You may withdraw consent for future processing at any time, without affecting earlier lawful processing.",
      ],
    },
    {
      id: "privacy-disclosure",
      title: "When we disclose information",
      paragraphs: [
        "We disclose only the information reasonably needed to providers supporting payment, hosting, order administration, delivery, communications, professional advice, analytics if later enabled with required controls, and customer care. Providers may use information for our instructions and their legal duties; some, such as payment networks and carriers, also act independently for parts of their service.",
        "We may also disclose information to comply with law or valid process, protect customers or the service, investigate fraud or safety issues, establish or defend legal claims, or support a merger, financing, restructuring, or sale subject to appropriate safeguards. We do not publish customer information as a matter of course.",
      ],
    },
    {
      id: "privacy-sale",
      title: "Sale, sharing, and advertising",
      paragraphs: [
        "We do not currently sell personal information for money or share it for cross-context behavioral advertising. This storefront does not currently use targeted-advertising trackers or use sensitive information to infer personal characteristics. If these practices change, we will update this policy and provide any legally required choice before the change applies.",
      ],
    },
    {
      id: "privacy-transfers",
      title: "International transfers",
      paragraphs: [
        `Because we serve customers and use providers in more than one place, information may be processed outside your country or region, including in ${siteConfig.operatingRegion.country} and locations used by our service providers. Privacy protections can differ. Where required, we use contractual, organizational, or other lawful transfer safeguards and assess the information and provider involved.`,
      ],
    },
    {
      id: "privacy-storage",
      title: "Cookies and browser storage",
      paragraphs: [
        "This storefront uses local storage for cart and wishlist items and session storage for limited payment-return information. The current storefront code does not set optional analytics or advertising cookies. A payment provider or other service may use its own necessary technologies after you use that service.",
      ],
      links: [{ prefix: "The current keys, purposes, and controls are listed in our ", to: "/cookies", label: "cookie policy", suffix: "." }],
    },
    {
      id: "privacy-retention",
      title: "Retention and security",
      paragraphs: [
        "We keep personal information only as long as reasonably needed for the purpose collected, including fulfillment, customer support, warranty history, fraud prevention, legal claims, accounting, tax, and other lawful recordkeeping. Browser-stored cart and wishlist data remains on your device until you clear it or the browser removes it; payment-return information is held for the browser session.",
        "We use administrative, technical, and organizational measures appropriate to the information and risk. No online system is perfectly secure, so we cannot guarantee absolute protection. If a breach creates a notification duty, we will notify affected people and authorities as required.",
      ],
    },
    {
      id: "privacy-children",
      title: "Children",
      paragraphs: [
        "The storefront is intended for a general adult shopping audience and is not directed to children under 16. A person who cannot form a binding purchase contract should use the service only with a parent or legal guardian. If you believe a child provided personal information without appropriate authorization, contact us so we can review and delete it where required.",
      ],
    },
    {
      id: "privacy-rights",
      title: "Your choices and rights",
      paragraphs: [
        "Depending on where you live, you may ask to access, correct, delete, restrict, object to, or receive a portable copy of personal information; withdraw consent; opt out of direct marketing; or complain to a privacy authority. We may verify your identity and retain information needed to document the request or comply with law.",
        "People in Mexico may request access to, rectification or cancellation of, or opposition to the processing of their personal data (ARCO rights), subject to the law applicable to the request. Other regional rights are described on our request page. We will not unlawfully discriminate or retaliate because you exercised a privacy right.",
      ],
      links: [{ prefix: "Use our ", to: "/privacy-rights", label: "privacy choices & requests page", suffix: " for instructions." }],
    },
    {
      id: "privacy-updates",
      title: "Policy updates and contact",
      paragraphs: [
        "We may revise this policy when the storefront, providers, or legal requirements change. The date above identifies the latest version. If a change materially affects how already-collected information is used, we will provide additional notice or seek consent where required.",
      ],
      email: true,
      emailSuffix: `. You may also contact us in ${siteConfig.contact.address}.`,
    },
  ],
  related: [
    { to: "/privacy-rights", label: "Privacy requests" },
    { to: "/cookies", label: "Cookie policy" },
    { to: "/security", label: "Security disclosure" },
  ],
};

function Privacy() {
  return <PolicyTemplate policy={privacyPolicy} />;
}

export default Privacy;
